import { useQuery, useMutation, useQueryClient } from "react-query";
import { doc, getDoc, setDoc, serverTimestamp, Timestamp } from "firebase/firestore";
import { db } from "../services/firebase";

export interface AppVersionConfig {
  id: string;
  minimumSupportedVersion: string;
  latestVersion: string;
  updateMessage: string | null;
  updatedAt?: {
    _seconds: number;
    _nanoseconds: number;
  };
}

interface UpdateAppVersionRequest {
  minimumSupportedVersion: string;
  latestVersion: string;
  updateMessage: string | null;
}

// Firestore から直接アプリバージョン設定を取得
const fetchAppVersion = async (): Promise<AppVersionConfig | null> => {
  const docRef = doc(db, "appVersions", "current");
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const data = docSnap.data();
    const updatedAt = data.updatedAt as Timestamp | null;
    return {
      id: docSnap.id,
      minimumSupportedVersion: data.minimumSupportedVersion,
      latestVersion: data.latestVersion,
      updateMessage: data.updateMessage || null,
      updatedAt: updatedAt
        ? { _seconds: updatedAt.seconds, _nanoseconds: updatedAt.nanoseconds }
        : undefined,
    };
  }
  return null;
};

// Firestore に直接アプリバージョン設定を更新
const updateAppVersion = async (data: UpdateAppVersionRequest): Promise<void> => {
  const docRef = doc(db, "appVersions", "current");
  await setDoc(
    docRef,
    {
      minimumSupportedVersion: data.minimumSupportedVersion,
      latestVersion: data.latestVersion,
      updateMessage: data.updateMessage || null,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

export const useAppVersion = () => {
  return useQuery<AppVersionConfig | null, Error>("appVersion", fetchAppVersion, {
    staleTime: 5 * 60 * 1000, // 5分
  });
};

export const useUpdateAppVersion = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, UpdateAppVersionRequest>(updateAppVersion, {
    onSuccess: () => {
      queryClient.invalidateQueries("appVersion");
    },
  });
};
